/**
 * SMSPRO-178 — Farbauswahl und Lesbarkeit der TopBar-Chips.
 *
 * Die Schriftfarbe wird NICHT mitgepflegt, sondern aus der Hintergrundfarbe
 * berechnet. Sonst laesst sich in der Verwaltung „weiss auf gelb" einstellen und
 * niemand merkt es, bis der Chip im Betrieb unlesbar in der Kopfzeile steht.
 */

/** Auswahlpalette der Verwaltung. Werte sind die persistierten Hex-Codes. */
export const CHIP_COLOR_PRESETS: { value: string; labelKey: string; fallback: string }[] = [
  { value: '#1976d2', labelKey: 'topbarChips.colors.blue', fallback: 'Blau' },
  { value: '#2e7d32', labelKey: 'topbarChips.colors.green', fallback: 'Grün' },
  { value: '#fbc02d', labelKey: 'topbarChips.colors.yellow', fallback: 'Gelb' },
  { value: '#ed6c02', labelKey: 'topbarChips.colors.orange', fallback: 'Orange' },
  { value: '#e53935', labelKey: 'topbarChips.colors.red', fallback: 'Rot' },
  { value: '#6a1b9a', labelKey: 'topbarChips.colors.purple', fallback: 'Violett' },
  { value: '#00838f', labelKey: 'topbarChips.colors.teal', fallback: 'Türkis' },
  { value: '#546e7a', labelKey: 'topbarChips.colors.grey', fallback: 'Grau' },
];

export const DEFAULT_CHIP_COLOR = '#1976d2';

/** '#rgb' und '#rrggbb' zu {r,g,b}. null bei unbrauchbarer Eingabe. */
function parseHex(hex: string): { r: number; g: number; b: number } | null {
  if (typeof hex !== 'string') return null;
  const value = hex.trim().replace(/^#/, '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/**
 * Schriftfarbe mit ausreichendem Kontrast zum Hintergrund.
 * Relative Helligkeit nach WCAG; Schwelle 0.5 trennt helle von dunklen Flaechen.
 * Unbrauchbare Farbe -> weisse Schrift (der Chip faellt dann auf den Standardton
 * zurueck, siehe chipBackground).
 */
export function chipTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#ffffff';

  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);

  return luminance > 0.5 ? 'rgba(0,0,0,0.87)' : '#ffffff';
}

/** Hintergrundfarbe mit Rueckfall, damit ein Datenfehler keinen unsichtbaren Chip erzeugt. */
export function chipBackground(hex: string | null | undefined): string {
  return hex && parseHex(hex) ? hex : DEFAULT_CHIP_COLOR;
}
